import React, { useState, useEffect } from "react";
import WarningModal from "../modal/index";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedAddress, setEditingAddress } from "../../redux/features/customer/customerSlice";
import { setEditingAddress as setCateringEditingAddress } from "../../redux/features/cateringCustomer/cateringCustomerSlice";
import {
  getBrandsByAreaAsync,
  checkItemAvailabilityAsync,
} from "../../redux/features/restaurants/restaurantThunks";
import {
  selectCartItems,
  selectCartRestaurantDetails,
  selectUnavailableItems,
} from "../../redux/features/restaurants/restaurantSelectors";
import { selectBrandsList } from "../../redux/features/restaurants/restaurantSelectors";
import { selectServiceType } from "../../redux/features/customer/customerSelectors";
import { setOrderType } from "../../redux/features/order/orderSlice";
// import { Link } from "react-router-dom";

const AddressBlock = ({
  address,
  isAddressBlockVisible,
  isSelected,
  onSelect,
  selectedAddress,
  isCheckout,
  showSwitchButton,
  setShowAddressForm = null,
}) => {
  const {
    firstLine,
    houseName,
    buildingNumber,
    floor,
    number,
    area,
    id,
    active_order,
    address_type,
  } = address;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [localSelected, setLocalSelected] = useState(isSelected);

  const dispatch = useDispatch();

  const cartItems = useSelector(selectCartItems);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const unavailableItems = useSelector(selectUnavailableItems);
  const brandsList = useSelector(selectBrandsList);
  const serviceType = useSelector(selectServiceType);

  // Update local selection state when prop changes
  useEffect(() => {
    setLocalSelected(isSelected);
  }, [isSelected]);

  // Initialize brands on mount if this is the selected address
  // Skip for catering orders as all brands support catering
  useEffect(() => {
    if (isSelected && !hasInitialized && !brandsList && 
        serviceType !== "individual catering" && serviceType !== "corporate catering") {
      // Trigger the brands fetching for the selected address on component mount
      dispatch(
        getBrandsByAreaAsync({
          areaName: area,
          serviceType: "delivery",
        })
      );
      setHasInitialized(true);
    }
  }, [isSelected, hasInitialized, area, dispatch, brandsList, serviceType]);

  const handleBlockSelection = () => {
    if (!isCheckout) {
      // Set order type to asap whenever an address is selected
      dispatch(setOrderType("asap"));
      
      if (isSelected) {
        // If this address is already selected, deselect it
        setLocalSelected(false);
        onSelect(null); // Pass null to indicate deselection
        // We'll let the parent component handle the Redux dispatch
      } else {
        // If this address is not selected, select it
        setLocalSelected(true);
        
        // Let the parent component handle the Redux dispatch and area check
        onSelect(address.id); // Pass the address ID for selection
      }
    }
  };

  const handleEditAddress = (e) => {
    e.stopPropagation(); // Prevent triggering address selection
    
    // Determine if we're in catering context based on service type
    const isCateringContext = serviceType === "individual catering" || serviceType === "corporate catering";
    
    // Use appropriate action based on context
    if (isCateringContext) {
      dispatch(setCateringEditingAddress(address)); // Set the address being edited in catering Redux
    } else {
      dispatch(setEditingAddress(address)); // Set the address being edited in regular Redux
    }
    
    // Show the address form (works for both customer and company contexts)
    if (setShowAddressForm) {
      setShowAddressForm(true);
    }
  };

  const handleSwitchAddress = async () => {
    // Only check availability in checkout mode
    if (isCheckout && cartItems.length > 0) {
      // Get all item IDs from the cart
      const itemIds = cartItems.map((item) => item.id);

      // Check item availability at new address
      const response = await dispatch(
        checkItemAvailabilityAsync({
          restaurant_id: cartRestaurantDetails.restaurantId,
          branch_id: cartRestaurantDetails.branchId,
          item_ids: itemIds,
        })
      );

      // Check if there are unavailable items
      const unavailableItemIds = response.payload.unavailable_item_ids || [];

      if (unavailableItemIds.length > 0) {
        // Show modal if there are unavailable items
        setIsModalVisible(true);
      } else {
        // If no items are unavailable, directly select the address
        setLocalSelected(true);
        onSelect(address.id); // Pass the address ID for selection
      }
    } else {
      // Direct selection for non-checkout mode
      setLocalSelected(true);
      onSelect(address.id); // Pass the address ID for selection
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleProceed = () => {
    setIsModalVisible(false);
    // Change the selected address
    setLocalSelected(true);
    onSelect(address.id); // Pass the address ID for selection

    // Additional logic for removing unavailable items from cart would go here
    // This would call an action to remove the unavailable items
  };

  // Use local state for determining UI appearance
  const displayAsSelected = localSelected;

  // Function to render the appropriate icon based on address type
  const renderAddressIcon = () => {
    const iconColor = displayAsSelected ? "text-white" : "text-slate-600";
    const addressTypeNormalized = address_type?.toLowerCase() || "house";

    if (addressTypeNormalized === "apartment") {
      return (
        <svg className={`w-3.5 h-3.5 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
        </svg>
      );
    } else if (addressTypeNormalized === "office") {
      return (
        <svg className={`w-3.5 h-3.5 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
          <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
        </svg>
      );
    } else {
      // Default to house icon
      return (
        <svg className={`w-3.5 h-3.5 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    }
  };

  return (
    <div className="mb-6">
      {/* Address Block */}
      <div
        className={`flex flex-col ${
          displayAsSelected ? "mb-3" : ""
        }`}
      >
        {isAddressBlockVisible && (
          <div
            className={`min-w-[257px] max-w-[257px] w-[257px] h-[220px] border-2 p-4 rounded-xl flex flex-col cursor-pointer relative overflow-hidden mx-auto sm:mx-0 transition-all duration-300 bg-white ${
              displayAsSelected 
                ? "border-[#86604D] shadow-lg" 
                : "border-slate-300 hover:border-gray-400 hover:shadow-md"
            }`}
            onClick={handleBlockSelection}
          >
            {/* Selected Badge */}
            {displayAsSelected && (
              <div className="absolute top-0 right-0 bg-[#86604D] text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-xs font-semibold flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Selected
              </div>
            )}

            {/* Active Order Badge */}
            {active_order === "true" && (
              <div className="absolute top-2 left-2 bg-emerald-600 text-white px-2.5 py-1 rounded-full font-semibold text-xs flex items-center gap-1 shadow-sm">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                Active Order
              </div>
            )}
            
            {/* Address Content */}
            <div className="flex-1 space-y-1.5 mt-8 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
              {/* Address Title with Icon */}
              <div className="flex items-center gap-1.5">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  displayAsSelected ? "bg-[#86604D]" : "bg-slate-200"
                }`}>
                  {renderAddressIcon()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-base truncate ${displayAsSelected ? "text-[#86604D]" : "text-gray-700"}`}>
                    {firstLine}
                  </h3>
                </div>
              </div>

              {/* Area with Icon */}
              <div className="flex items-center gap-2 text-sm">
                <svg className={`w-4 h-4 flex-shrink-0 ${displayAsSelected ? "text-[#86604D]" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`truncate font-medium ${displayAsSelected ? "text-[#86604D]" : "text-gray-600"}`}>
                  {area}
                </span>
              </div>

              {/* Address Details */}
              <div className="text-xs space-y-1 text-gray-600">
                {houseName && (
                  <div className="flex items-start gap-2">
                    <svg className={`w-3 h-3 mt-0.5 flex-shrink-0 ${displayAsSelected ? "text-[#86604D]" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                    </svg>
                    <div className="truncate">{houseName}</div>
                  </div>
                )}
                {buildingNumber && (
                  <div className="flex items-center gap-2">
                    <svg className={`w-3 h-3 flex-shrink-0 ${displayAsSelected ? "text-[#86604D]" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate">{buildingNumber}</span>
                  </div>
                )}
                {floor && (
                  <div className="flex items-center gap-2">
                    <svg className={`w-3 h-3 flex-shrink-0 ${displayAsSelected ? "text-[#86604D]" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate">{floor}</span>
                  </div>
                )}
                {number && (
                  <div className="flex items-center gap-2">
                    <svg className={`w-3 h-3 flex-shrink-0 ${displayAsSelected ? "text-[#86604D]" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                    <span className="truncate">Tel: {number}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Edit Button - Only show in non-checkout mode */}
            {!isCheckout && (
              <button
                onClick={handleEditAddress}
                className={`absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center rounded-full transition-all ${
                  displayAsSelected 
                    ? "bg-[#86604D] hover:bg-[#6B4A3A] text-white shadow-md" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                }`}
                title="Edit Address"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Switch Address Button - Show based on conditions */}
      {isCheckout && !displayAsSelected ? (
        <div className="transition-all duration-300 py-1 flex justify-center">
          <button
            onClick={handleSwitchAddress}
            className="py-2 px-4 bg-[#86604D] hover:bg-[#6B4A3A] text-white rounded-lg shadow-md hover:shadow-lg text-center max-w-[257px] w-full font-semibold transition-all duration-200"
          >
            {isCheckout ? "Switch to this address" : "Switch Address"}
          </button>
        </div>
      ) : null}

      {/* Warning Modal */}
      {isModalVisible && (
        <WarningModal
          onCancel={handleCancel}
          onProceed={handleProceed}
          description="You are about to change the selected address!"
          subDescription="Below items cannot be delivered to your new location and will be removed from cart."
          showItems={unavailableItems.length > 0}
          unavailableItems={unavailableItems}
          cartItems={cartItems}
          isLoading={false}
        />
      )}
    </div>
  );
};

export default AddressBlock;
